'use client';

import { User } from '@greet/shared';
import { setUser as setSentryUser } from '@sentry/nextjs';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { identifyUser, initMixpanel, resetUser } from '../lib/mixpanel';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize Mixpanel
    initMixpanel();

    // Check for existing auth data on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);

        // Identify user in Mixpanel
        identifyUser(userData.id, {
          email: userData.email,
          name: userData.businessName,
        });

        // Identify user in Sentry
        setSentryUser({
          id: userData.id,
          email: userData.email,
          username: userData.email,
          businessId: userData.businessId,
          businessName: userData.businessName,
          isAdmin: userData.isAdmin,
        });
      } catch (error) {
        console.error('Error restoring auth state:', error);
        // Clear invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    setIsInitialized(true);
  }, []);

  const login = (newToken: string, userData: User) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Identify user in Mixpanel
    identifyUser(userData.id, {
      email: userData.email,
      name: userData.businessName,
      businessId: userData.businessId,
      isAdmin: userData.isAdmin,
    });

    // Identify user in Sentry
    setSentryUser({
      id: userData.id,
      email: userData.email,
      username: userData.email,
      businessId: userData.businessId,
      businessName: userData.businessName,
      isAdmin: userData.isAdmin,
    });
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Reset Mixpanel user
    resetUser();

    // Reset Sentry user
    setSentryUser(null);

    // router cannot be used, must use window.location.href
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        isInitialized,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 
